import { useLanguage } from "@/context/language"
import {
  buildIntegrationConnectUrl,
  createIntegrationConnectSession,
  datoxAgentMode,
  deleteIntegrationConnection,
  deleteFile,
  getFileContentBlob,
  listFileIntegrations,
  listFiles,
  listIntegrationConnections,
  listPeriods,
  listProjects,
  type DatoxFile,
  type DatoxProject,
} from "@/datox/api"
import { FileIcon } from "@opencode-ai/ui/file-icon"
import { Icon as IconV2 } from "@opencode-ai/ui/v2/icon"
import { IconButtonV2 } from "@opencode-ai/ui/v2/icon-button-v2"
import { MenuV2 } from "@opencode-ai/ui/v2/menu-v2"
import { TooltipV2 } from "@opencode-ai/ui/v2/tooltip-v2"
import { useLocation, useNavigate } from "@solidjs/router"
import { createMemo, createResource, For, onCleanup, onMount, Show, type JSX } from "solid-js"
import { createStore } from "solid-js/store"
import "@/datox/files-library.css"
import "@/datox/workspace-listing.css"
import "@/datox/capability-library.css"

type LibraryFile = DatoxFile & {
  projectName: string
}

type LibraryFolder = {
  id: string
  name: string
  kind: "project" | "period"
  count: number
}

type LibraryFileType = "all" | "images" | "documents" | "spreadsheets"

type DatoxIntegrationCatalogItem = {
  label: string
  logoPath?: string
  description: keyof typeof import("@/i18n/en").dict
  canConnect: boolean
  providerKey?: string
  managed?: boolean
}

export function DatoxNavigationShell(props: { children: JSX.Element }) {
  const language = useLanguage()
  const navigate = useNavigate()
  const location = useLocation()
  const [store, setStore] = createStore({
    collapsed: storedNavigationCollapsed(),
    width: storedNavigationWidth(),
    compact: false,
  })
  const compactLibrary = () => store.compact && (["/overview", "/files", "/chats", "/projects", "/agents", "/integrations", "/automations"].includes(location.pathname) || location.pathname.startsWith("/projects/") || location.pathname.startsWith("/automations/") || /^\/catalog\/(reports|workflows|dashboards)(\/|$)/.test(location.pathname))
  const collapsed = () => store.collapsed || compactLibrary()

  onMount(() => {
    const media = window.matchMedia("(max-width: 700px)")
    const updateCompact = () => setStore("compact", media.matches)
    updateCompact()
    media.addEventListener("change", updateCompact)
    const navigationListener = (event: Event) => {
      const detail = (event as CustomEvent<{ collapsed?: boolean }>).detail
      setStore("collapsed", detail?.collapsed ?? storedNavigationCollapsed())
    }
    window.addEventListener("datox:navigation-toggle", navigationListener)
    onCleanup(() => {
      media.removeEventListener("change", updateCompact)
      window.removeEventListener("datox:navigation-toggle", navigationListener)
    })
  })

  const openFiles = () => navigate("/files")
  const openIntegrations = () => navigate("/integrations")

  const navigationToggle = () => (
    <TooltipV2 placement={store.collapsed ? "right" : "bottom"} value={language.t(store.collapsed ? "datox.navigation.expand" : "datox.navigation.collapse")}>
      <IconButtonV2
        type="button"
        variant="ghost-muted"
        size="large"
        class="!h-9 !w-9 shrink-0 rounded-full border border-v2-border-border-base bg-v2-background-bg-layer-01"
        icon={<IconV2 name="sidebar-right" classList={{ "rotate-180": !store.collapsed }} />}
        onClick={toggleNavigation}
        aria-label={language.t(store.collapsed ? "datox.navigation.expand" : "datox.navigation.collapse")}
        title={language.t(store.collapsed ? "datox.navigation.expand" : "datox.navigation.collapse")}
      />
    </TooltipV2>
  )

  const startResize = (event: PointerEvent) => {
    event.preventDefault()
    const startX = event.clientX
    const startWidth = store.width
    const move = (moveEvent: PointerEvent) => {
      const next = Math.min(420, Math.max(240, startWidth + moveEvent.clientX - startX))
      setStore("width", next)
      localStorage.setItem("datox.agent.navigationWidth", String(next))
    }
    const up = () => {
      window.removeEventListener("pointermove", move)
      window.removeEventListener("pointerup", up)
    }
    window.addEventListener("pointermove", move)
    window.addEventListener("pointerup", up)
  }

  if (!datoxAgentMode()) return <>{props.children}</>

  return (
    <div class="flex min-h-0 min-w-0 flex-1 self-stretch overflow-hidden gap-0 p-2 pt-0">
      <aside
        data-component="datox-navigation"
        class="relative my-2 min-h-0 shrink-0 overflow-hidden rounded-[10px] border border-v2-border-border-base bg-v2-background-bg-base shadow-[var(--v2-elevation-raised)] transition-[width] duration-150 ease-out"
        classList={{ "w-12": collapsed() }}
        style={collapsed() ? undefined : { width: `${store.width}px` }}
      >
        <Show
          when={!collapsed()}
          fallback={
            <div class="flex h-full flex-col items-center gap-3 px-1.5 py-4">
              <Show when={!compactLibrary()}>{navigationToggle()}</Show>
              <div class="h-px w-9 bg-v2-border-border-base" />
              <For each={[
                { label: language.t("datox.overview.title"), icon: "workspace", action: () => navigate("/overview"), active: location.pathname === "/overview" },
                { label: language.t("home.projects"), icon: "filetree", action: () => navigate("/projects") },
                { label: language.t("datox.chats.title"), icon: "edit", action: () => navigate("/chats") },
                { label: language.t("datox.navigation.files"), icon: "folder", action: openFiles },
                { label: language.t("datox.integrations.title"), icon: "settings-gear", action: openIntegrations },
                { label: language.t("datox.catalog.dashboards"), icon: "grid-plus", action: () => navigate("/catalog/dashboards") },
                 { label: language.t("datox.catalog.reports"), icon: "report", action: () => navigate("/catalog/reports") },
                 { label: language.t("datox.approvals.title"), icon: "check", action: () => navigate("/approvals") },
                  { label: language.t("datox.catalog.workflows"), icon: "branch", action: () => navigate("/catalog/workflows") },
                  { label: language.t("datox.automations.title"), icon: "clock", action: () => navigate("/automations") },
              ]}>
                {(item) => (
                  <TooltipV2 placement="right" value={item.label}>
                    <button type="button" class="flex size-8 shrink-0 items-center justify-center rounded-lg text-v2-text-text-muted hover:bg-v2-overlay-simple-overlay-hover hover:text-v2-text-text-base" classList={{ "bg-v2-background-bg-layer-03": item.active }} aria-current={item.active ? "page" : undefined} onClick={item.action} aria-label={item.label}>
                      <IconV2 name={item.icon} size="normal" />
                    </button>
                  </TooltipV2>
                )}
              </For>
            </div>
          }
        >
          <div class="flex h-full min-h-0 flex-col gap-5 overflow-y-auto px-4 py-5 [scrollbar-color:var(--v2-border-border-base)_transparent] [scrollbar-gutter:stable] [scrollbar-width:thin]">
            <div class="flex h-9 shrink-0 items-center justify-between gap-2">
              <span class="min-w-0 truncate pl-2 text-14-medium text-v2-text-text-base">{language.t("datox.navigation.myWorkspace")}</span>
              {navigationToggle()}
            </div>
            <div class="flex flex-col gap-1">
              <button type="button" class="flex h-9 items-center gap-2 rounded-md px-2 text-left text-14-regular text-v2-text-text-base hover:bg-v2-overlay-simple-overlay-hover" classList={{ "bg-v2-background-bg-layer-03": location.pathname === "/overview" }} aria-current={location.pathname === "/overview" ? "page" : undefined} onClick={() => navigate("/overview")}>
                <IconV2 name="workspace" size="normal" class="shrink-0 text-v2-text-text-muted" />
                <span>{language.t("datox.overview.title")}</span>
              </button>
              <button type="button" class="flex h-9 items-center gap-2 rounded-md px-2 text-left text-14-regular text-v2-text-text-base hover:bg-v2-overlay-simple-overlay-hover" classList={{ "bg-v2-background-bg-layer-03": location.pathname.startsWith("/projects") }} aria-current={location.pathname.startsWith("/projects") ? "page" : undefined} onClick={() => navigate("/projects")}>
                <IconV2 name="filetree" size="normal" class="shrink-0 text-v2-text-text-muted" />
                <span>{language.t("home.projects")}</span>
              </button>
              <button type="button" class="flex h-9 items-center gap-2 rounded-md px-2 text-left text-14-regular text-v2-text-text-base hover:bg-v2-overlay-simple-overlay-hover" classList={{ "bg-v2-background-bg-layer-03": location.pathname === "/chats" }} aria-current={location.pathname === "/chats" ? "page" : undefined} onClick={() => navigate("/chats")}>
                <IconV2 name="edit" size="normal" class="shrink-0 text-v2-text-text-muted" />
                <span>{language.t("datox.chats.title")}</span>
              </button>
              <button type="button" class="flex h-9 items-center gap-2 rounded-md px-2 text-left text-14-regular text-v2-text-text-base hover:bg-v2-overlay-simple-overlay-hover" classList={{ "bg-v2-background-bg-layer-03": location.pathname === "/files" }} aria-current={location.pathname === "/files" ? "page" : undefined} onClick={openFiles}>
                <IconV2 name="folder" size="normal" class="shrink-0 text-v2-text-text-muted" />
                <span>{language.t("datox.navigation.files")}</span>
              </button>
              <button type="button" class="flex h-9 items-center gap-2 rounded-md px-2 text-left text-14-regular text-v2-text-text-base hover:bg-v2-overlay-simple-overlay-hover" classList={{ "bg-v2-background-bg-layer-03": location.pathname === "/integrations" }} aria-current={location.pathname === "/integrations" ? "page" : undefined} onClick={openIntegrations}>
                <IconV2 name="settings-gear" size="normal" class="shrink-0 text-v2-text-text-muted" />
                <span>{language.t("datox.integrations.title")}</span>
              </button>
              <div class="flex flex-col gap-1">
                <For each={["dashboards", "reports", "approvals", "workflows"] as const}>
                  {(kind) => (
                    <button
                      type="button"
                      class="flex h-9 items-center gap-2 rounded-md px-2 text-left text-14-regular text-v2-text-text-base hover:bg-v2-overlay-simple-overlay-hover"
                      classList={{ "bg-v2-background-bg-layer-03": kind === "approvals" ? location.pathname.startsWith("/approvals") : location.pathname.startsWith(`/catalog/${kind}`) || (kind === "workflows" && location.pathname.startsWith("/workflows")) }}
                      aria-current={(kind === "approvals" ? location.pathname.startsWith("/approvals") : location.pathname.startsWith(`/catalog/${kind}`) || (kind === "workflows" && location.pathname.startsWith("/workflows"))) ? "page" : undefined}
                      onClick={() => navigate(kind === "approvals" ? "/approvals" : `/catalog/${kind}`)}
                    >
                      <IconV2 name={kind === "dashboards" ? "grid-plus" : kind === "reports" ? "report" : kind === "approvals" ? "check" : "branch"} size="normal" class="shrink-0 text-v2-text-text-muted" />
                      {kind === "approvals" ? language.t("datox.approvals.title") : language.t(`datox.catalog.${kind}`)}
                    </button>
                  )}
                </For>
                <button type="button" class="flex h-9 items-center gap-2 rounded-md px-2 text-left text-14-regular text-v2-text-text-base hover:bg-v2-overlay-simple-overlay-hover" classList={{ "bg-v2-background-bg-layer-03": location.pathname.startsWith("/automations") }} aria-current={location.pathname.startsWith("/automations") ? "page" : undefined} onClick={() => navigate("/automations")}><IconV2 name="clock" size="normal" class="shrink-0 text-v2-text-text-muted" />{language.t("datox.automations.title")}</button>
              </div>
            </div>
          </div>
        </Show>
        <Show when={!collapsed()}>
          <div
            class="absolute right-0 top-0 h-full w-2 cursor-col-resize bg-transparent hover:bg-v2-border-border-base/50"
            onPointerDown={startResize}
            aria-label={language.t("datox.navigation.resize")}
            role="separator"
          />
        </Show>
      </aside>
      <div class="flex min-w-0 flex-1 overflow-hidden">{props.children}</div>
    </div>
  )
}

export function DatoxFilesLibrary(props: { projects: DatoxProject[] }) {
  const language = useLanguage()
  const [store, setStore] = createStore({ search: "", type: "all" as LibraryFileType, projectID: "", periodID: "", view: "folders" as "folders" | "files", sort: "newest", downloading: "", actionError: false })
  const selectedProject = createMemo(() => props.projects.find((project) => project.id === store.projectID))
  const [projectPeriods] = createResource(() => store.projectID || undefined, listPeriods)
  const [files, filesActions] = createResource(
    () => props.projects,
    async (projects) =>
      (
        await Promise.all(
          projects.map((project) =>
            listFiles(project.id)
              .then((files) => files.map((file) => ({ ...file, projectName: project.name })))
              .catch(() => [] as LibraryFile[]),
          ),
        )
      )
        .flat()
        .sort((a, b) => Date.parse(b.updated_at) - Date.parse(a.updated_at)),
  )
  const filteredFiles = createMemo(() => {
    const query = store.search.trim().toLowerCase()
    return (files.latest ?? []).filter((file) => {
      const matchesSearch = !query || `${file.filename} ${file.projectName} ${file.content_type}`.toLowerCase().includes(query)
      return matchesSearch && fileTypeMatches(file, store.type)
    })
  })
  const scopedFiles = createMemo(() => filteredFiles().filter((file) => (!store.projectID || file.project_id === store.projectID) && (!store.periodID || (store.periodID === "__none__" ? !file.period_id : file.period_id === store.periodID))))
  const projectFolders = createMemo<LibraryFolder[]>(() => props.projects.flatMap((project) => {
    const count = filteredFiles().filter((file) => file.project_id === project.id).length
    return count > 0 ? [{ id: project.id, name: project.name, kind: "project", count }] : []
  }))
  const periodFolders = createMemo<LibraryFolder[]>(() => {
    const projectID = store.projectID
    if (!projectID) return []
    const projectFiles = filteredFiles().filter((file) => file.project_id === projectID)
    const folders = (projectPeriods() ?? []).flatMap((period) => {
      const count = projectFiles.filter((file) => file.period_id === period.id).length
      return count > 0 ? [{ id: period.id, name: period.name, kind: "period" as const, count }] : []
    })
    const unassignedCount = projectFiles.filter((file) => !file.period_id).length
    return unassignedCount > 0 ? [...folders, { id: "__none__", name: language.t("datox.files.library.projectLevel"), kind: "period", count: unassignedCount }] : folders
  })
  const showFlatSearch = createMemo(() => store.search.trim().length > 0 || store.view === "files")
  const visibleFolders = createMemo(() => {
    if (showFlatSearch()) return []
    if (!store.projectID) return projectFolders()
    if (!store.periodID) return periodFolders()
    return []
  })
  const visibleFiles = createMemo(() => {
    const values = showFlatSearch() || (store.projectID && store.periodID) ? [...scopedFiles()] : []
    if (store.sort === "name") return values.sort((a, b) => a.filename.localeCompare(b.filename))
    if (store.sort === "size") return values.sort((a, b) => b.size - a.size)
    return values.sort((a, b) => Date.parse(b.updated_at) - Date.parse(a.updated_at))
  })
  const browsingFolders = () => !showFlatSearch() && !store.periodID
  const openFolder = (folder: LibraryFolder) => {
    if (folder.kind === "project") {
      setStore({ projectID: folder.id, periodID: "" })
      return
    }
    setStore("periodID", folder.id)
  }
  const resetPath = () => setStore({ projectID: "", periodID: "" })
  const selectedPeriodName = createMemo(() => store.periodID === "__none__" ? language.t("datox.files.library.projectLevel") : (projectPeriods() ?? []).find((period) => period.id === store.periodID)?.name ?? "")
  const download = async (file: LibraryFile) => {
    if (store.downloading) return
    setStore({ downloading: file.id, actionError: false })
    try {
      const blob = await getFileContentBlob(file.project_id, file.id)
      const url = URL.createObjectURL(blob)
      const anchor = document.createElement("a")
      anchor.href = url
      anchor.download = file.filename
      anchor.click()
      URL.revokeObjectURL(url)
    } catch {
      setStore("actionError", true)
    } finally {
      setStore("downloading", "")
    }
  }
  const remove = (file: LibraryFile) => {
    setStore("actionError", false)
    void deleteFile(file.project_id, file.id).then(() => filesActions.refetch()).catch(() => setStore("actionError", true))
  }

  return (
    <div data-datox-page="files" data-datox-content="flush" class="m-2 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-[10px] bg-v2-background-bg-base text-v2-text-text-base shadow-[var(--v2-elevation-raised)]">
      <div class="files-library__header">
        <div class="files-library__heading"><span class="files-library__header-icon" aria-hidden="true"><IconV2 name="folder" size="large" /></span><div><h1>{language.t("datox.navigation.files")}</h1><p>{language.t("datox.files.library.description")}</p></div></div>
        <div class="files-library__summary"><span>{language.t("datox.files.library.totalFiles", { count: (files.latest ?? []).length })}</span><span>{language.t("datox.files.library.totalProjects", { count: props.projects.length })}</span><button type="button" class="files-library__button" disabled={files.loading} aria-busy={files.loading} onClick={() => void filesActions.refetch()}>{language.t("datox.files.library.refresh")}</button></div>
      </div>
      <div class="files-library__body">
        <div class="files-library__browser">
          <div class="files-library__toolbar">
            <label class="files-library__search">
              <input
                type="search"
                value={store.search}
                onInput={(event) => setStore("search", event.currentTarget.value)}
                placeholder={language.t("datox.files.library.search")}
                aria-label={language.t("datox.files.library.search")}
              />
              <span aria-hidden="true"><IconV2 name="magnifying-glass" size="normal" /></span>
            </label>
            <select class="files-library__project-filter" aria-label={language.t("datox.files.library.project")} value={store.projectID} onChange={(event) => setStore({ projectID: event.currentTarget.value, periodID: "" })}><option value="">{language.t("datox.files.library.allProjects")}</option><For each={props.projects}>{(project) => <option value={project.id}>{project.name}</option>}</For></select>
            <div class="files-library__filters" role="group" aria-label={language.t("datox.files.library.fileType")}>
              <For each={libraryFileTypes}>
                {(type) => (
                  <button
                    type="button"
                    aria-pressed={store.type === type}
                    onClick={() => setStore("type", type)}
                  >
                    {language.t(libraryFileTypeLabel(type))}
                  </button>
                )}
              </For>
            </div>
          </div>
            <div class="files-library__context">
            <nav class="files-library__breadcrumbs" aria-label={language.t("datox.files.library.location")}>
              <button type="button" aria-current={!store.projectID ? "location" : undefined} onClick={resetPath}>{language.t("datox.files.library.allProjects")}</button>
              <Show when={selectedProject()}>
                {(project) => (
                  <>
                    <span aria-hidden="true">/</span>
                    <button type="button" title={project().name} aria-current={!store.periodID ? "location" : undefined} onClick={() => setStore("periodID", "")}>{project().name}</button>
                  </>
                )}
              </Show>
              <Show when={selectedPeriodName()}>
                {(name) => (
                  <>
                    <span aria-hidden="true">/</span>
                    <span aria-current="location" title={name()}>{name()}</span>
                  </>
                )}
              </Show>
            </nav>
            <div class="files-library__view-controls"><div class="files-library__views" role="group" aria-label={language.t("datox.files.library.view")}><For each={["folders", "files"] as const}>{(view) => <button type="button" aria-pressed={store.view === view} onClick={() => setStore("view", view)}>{language.t(`datox.files.library.view.${view}`)}</button>}</For></div><Show when={!browsingFolders()}><select aria-label={language.t("datox.files.library.sort")} value={store.sort} onChange={(event) => setStore("sort", event.currentTarget.value)}><option value="newest">{language.t("datox.files.library.sort.newest")}</option><option value="name">{language.t("datox.files.library.sort.name")}</option><option value="size">{language.t("datox.files.library.sort.size")}</option></select></Show></div>
            </div>
          <Show when={store.actionError}><p role="alert" class="files-library__error">{language.t("datox.files.library.actionError")}</p></Show>
          <div class="files-library__columns" data-folders={browsingFolders()}>
            <div>{language.t("datox.files.library.name")}</div>
            <Show when={!browsingFolders()}><div class="files-library__project">{language.t("datox.files.library.project")}</div><div class="files-library__modified">{language.t("datox.files.library.modified")}</div></Show>
            <div class="files-library__size">{language.t(browsingFolders() ? "datox.navigation.files" : "datox.files.library.size")}</div>
            <div class="files-library__actions-label">{language.t("datox.files.library.actions")}</div>
          </div>
          <div class="files-library__list" aria-busy={files.loading || (!!store.projectID && !store.periodID && projectPeriods.loading)}>
            <Show when={!(files.loading && !files.latest) && !(browsingFolders() && store.projectID && projectPeriods.loading)} fallback={<div role="status" class="files-library__empty">{language.t("common.loading")}</div>}>
              <For each={visibleFolders()}>
                {(folder) => (
                  <button type="button" class="files-library__folder" onClick={() => openFolder(folder)}>
                    <div class="files-library__name">
                      <span class="files-library__icon">
                        <IconV2 name="folder" size="normal" class="text-v2-text-text-muted" />
                      </span>
                      <span class="files-library__name-copy"><span class="files-library__filename" title={folder.name}>{folder.name}</span><span class="files-library__subtitle">{language.t(folder.kind === "project" ? "datox.files.library.projectFolder" : "datox.files.library.periodFolder")}</span></span>
                    </div>
                    <span class="files-library__folder-count" aria-label={language.t("datox.files.library.totalFiles", { count: folder.count })}>{folder.count}</span>
                    <span class="files-library__open">{language.t("datox.files.library.open")}<span aria-hidden="true">→</span></span>
                  </button>
                )}
              </For>
              <For each={visibleFiles()} fallback={<Show when={visibleFolders().length === 0}><div role="status" class="files-library__empty"><IconV2 name="folder" size="large" /><p>{language.t(store.search.trim() || store.type !== "all" ? "datox.files.library.noMatches" : "datox.files.library.empty")}</p><Show when={store.search.trim() || store.type !== "all"}><button type="button" class="files-library__button" onClick={() => setStore({ search: "", type: "all" })}>{language.t("datox.files.library.clearFilters")}</button></Show></div></Show>}>
                {(file) => (
                  <div class="files-library__file">
                    <div class="files-library__name">
                      <span class="files-library__icon">
                        <FileIcon node={{ path: file.filename, type: "file" }} class="size-4 shrink-0" />
                      </span>
                      <span class="files-library__name-copy"><span class="files-library__filename" title={file.filename}>{file.filename}</span><span class="files-library__subtitle">{file.filename.includes(".") ? file.filename.split(".").pop()?.toUpperCase() : file.content_type}</span></span>
                    </div>
                    <div class="files-library__project" title={file.projectName}>{file.projectName}</div>
                    <time class="files-library__modified" datetime={file.updated_at}>{formatLibraryDate(file.updated_at)}</time>
                    <div class="files-library__size">{formatLibrarySize(file.size)}</div>
                    <div class="files-library__file-actions"><button type="button" class="files-library__download" disabled={!!store.downloading} aria-busy={store.downloading === file.id} aria-label={language.t("datox.files.library.downloadNamed", { name: file.filename })} onClick={() => void download(file)}>{language.t(store.downloading === file.id ? "datox.pipeline.report.downloading" : "datox.files.panel.download")}</button>
                    <MenuV2 gutter={4} placement="bottom-end">
                      <MenuV2.Trigger type="button" class="files-library__more" aria-label={language.t("datox.files.library.actionsNamed", { name: file.filename })}>
                          <IconV2 name="outline-dots" size="normal" />
                      </MenuV2.Trigger>
                      <MenuV2.Portal>
                        <MenuV2.Portal><MenuV2.Content>
                          <MenuV2.Item onSelect={() => download(file)}>
                            <span class="flex items-center gap-2"><IconV2 name="download" size="small" />{language.t("datox.files.panel.download")}</span>
                          </MenuV2.Item>
                          <MenuV2.Item onSelect={() => remove(file)}>
                            <span class="flex items-center gap-2" style={{ color: "var(--v2-state-fg-danger)" }}><IconV2 name="trash" size="small" />{language.t("common.delete")}</span>
                          </MenuV2.Item>
                        </MenuV2.Content></MenuV2.Portal>
                      </MenuV2.Portal>
                    </MenuV2></div>
                  </div>
                )}
              </For>
            </Show>
          </div>
          <div class="files-library__footer" role="status">{language.t("datox.files.library.totalFiles", { count: scopedFiles().length })}<Show when={store.search.trim() || store.type !== "all"}><button type="button" onClick={() => setStore({ search: "", type: "all" })}>{language.t("datox.files.library.clearFilters")}</button></Show></div>
        </div>
      </div>
    </div>
  )
}

export function DatoxIntegrationsPage() {
  const language = useLanguage()
  const [store, setStore] = createStore({ search: "", status: "all", view: "grid" as "grid" | "list", busy: "", actionError: false })
  const timers = new Set<ReturnType<typeof setTimeout>>()
  let active = true
  onCleanup(() => { active = false; timers.forEach(clearTimeout) })
  const [connections, connectionActions] = createResource(() => listIntegrationConnections())
  const [alphaProjects, alphaActions] = createResource(async () => {
    const projects = await listProjects()
    const configured = await Promise.all(projects.map(async (project) => ({ project, connections: await listFileIntegrations(project.id) })))
    return configured.filter(({ connections }) => connections.some((connection) => connection.provider === "alphadesk")).map(({ project }) => project.name)
  })
  const refreshConnections = () => {
    if (!connections.loading) void Promise.resolve(connectionActions.refetch()).catch(() => undefined)
    if (!alphaProjects.loading) void Promise.resolve(alphaActions.refetch()).catch(() => undefined)
  }
  onMount(() => {
    const visibilityRefresh = () => {
      if (document.visibilityState === "visible") refreshConnections()
    }
    window.addEventListener("focus", refreshConnections)
    document.addEventListener("visibilitychange", visibilityRefresh)
    onCleanup(() => {
      window.removeEventListener("focus", refreshConnections)
      document.removeEventListener("visibilitychange", visibilityRefresh)
    })
  })
  const connectionForProvider = (providerKey: string) => (connections.error ? [] : connections.latest?.connections ?? []).find((connection) => connection.provider_config_key === providerKey)
  const statusFor = (integration: DatoxIntegrationCatalogItem) => {
    if (integration.managed && alphaProjects.error) return "unknown"
    if (integration.managed && alphaProjects.loading && !alphaProjects.latest) return "checking"
    if (integration.managed) return alphaProjects.latest?.length ? "connected" : "managed"
    if (integration.providerKey && connections.error) return "unknown"
    if (integration.providerKey && connections.loading && !connections.latest) return "checking"
    if (integration.providerKey && connectionForProvider(integration.providerKey)) return "connected"
    return integration.canConnect ? "available" : "planned"
  }
  const filteredIntegrations = createMemo(() => {
    const query = store.search.trim().toLowerCase()
    return datoxIntegrationCatalog.filter((integration) => (!query || `${integration.label} ${language.t(integration.description, { name: integration.label })}`.toLowerCase().includes(query)) &&
      (store.status === "all" || statusFor(integration) === store.status))
  })
  const connect = (providerKey: string) => {
    if (store.busy) return
    setStore({ busy: providerKey, actionError: false })
    const popup = window.open("about:blank", "_blank")
    if (popup) popup.opener = null
    void createIntegrationConnectSession(providerKey).then((session) => {
      const url = buildIntegrationConnectUrl(session)
      if (popup) popup.location.href = url
      if (!popup) window.location.href = url
      if (!active) return
      for (const delay of [1500, 5000]) {
        const timer = setTimeout(() => { timers.delete(timer); void Promise.resolve(connectionActions.refetch()).catch(() => undefined) }, delay)
        timers.add(timer)
      }
    }).catch(() => { popup?.close(); setStore("actionError", true) }).finally(() => setStore("busy", ""))
  }
  const disconnect = (providerKey: string, connectionID: string) => {
    if (store.busy) return
    setStore({ busy: providerKey, actionError: false })
    void deleteIntegrationConnection(providerKey, connectionID).then(() => connectionActions.refetch()).catch(() => setStore("actionError", true)).finally(() => setStore("busy", ""))
  }
  return (
    <div data-datox-page="integrations" data-datox-content="flush" data-workspace-listing="integrations" class="m-2 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-[10px] bg-v2-background-bg-base text-v2-text-text-base shadow-[var(--v2-elevation-raised)]">
      <div class="listing-header">
        <span data-datox-icon class="capability-icon"><IconV2 name="cube" size="large" /></span><div><h1>{language.t("datox.integrations.title")}</h1><p class="text-v2-text-text-muted">{language.t("datox.integrations.description")}</p></div>
      </div>
      <div class="capability-section">
        <div class="listing-filters">
          <label class="listing-search">
            <input
              type="search"
              value={store.search}
              onInput={(event) => setStore("search", event.currentTarget.value)}
              placeholder={language.t("datox.integrations.search")}
              aria-label={language.t("datox.integrations.search")}
            />
            <IconV2 name="magnifying-glass" size="normal" class="pointer-events-none absolute right-3 top-3 text-v2-text-text-muted" />
          </label>
          <label class="listing-select"><span class="sr-only">{language.t("datox.integrations.statusFilter")}</span><select value={store.status} onChange={(event) => setStore("status", event.currentTarget.value)}><option value="all">{language.t("datox.integrations.all")}</option><For each={["connected", "available", "managed", "planned"] as const}>{(status) => <option value={status}>{language.t(`datox.integrations.${status}`)}</option>}</For></select></label>
          <button type="button" class="capability-button" disabled={connections.loading || alphaProjects.loading || !!store.busy} aria-busy={connections.loading || alphaProjects.loading} onClick={refreshConnections}>{language.t("datox.integrations.refresh")}</button>
        </div>
        <div class="listing-controls"><p role="status">{language.t("datox.listing.results", { count: filteredIntegrations().length, total: datoxIntegrationCatalog.length })}</p><Show when={store.search || store.status !== "all"}><button type="button" class="listing-clear" onClick={() => setStore({ search: "", status: "all" })}>{language.t("datox.listing.clear")}</button></Show><div class="listing-display"><div class="listing-view" role="group" aria-label={language.t("datox.listing.view")}><For each={["grid", "list"] as const}>{(view) => <button type="button" aria-pressed={store.view === view} onClick={() => setStore("view", view)}>{language.t(`datox.listing.${view}`)}</button>}</For></div></div></div>
        <Show when={connections.error || alphaProjects.error || store.actionError}><p role="alert" class="capability-error">{language.t(connections.error || alphaProjects.error ? "datox.integrations.loadError" : "datox.integrations.actionError")}</p></Show>
        <div class="listing-results">
          <div class="capability-grid" data-view={store.view}>
            <For each={filteredIntegrations()} fallback={<div class="capability-empty"><p>{language.t("datox.integrations.empty")}</p><button type="button" onClick={() => setStore({ search: "", status: "all" })}>{language.t("datox.listing.clear")}</button></div>}>
              {(integration) => (
                <div data-datox-card="integration" class="capability-card">
                  <div class="capability-card__heading">
                    <span class="capability-provider-icon">
                      <Show when={integration.logoPath} fallback={<IconV2 name="workspace" size="large" />}><img src={integration.logoPath} alt="" class="max-h-full max-w-full" /></Show>
                    </span>
                    <div class="min-w-0 flex-1">
                      <h3>{integration.label}</h3><span class="capability-status" data-status={statusFor(integration)}>{language.t(`datox.integrations.${statusFor(integration)}`)}</span>
                    </div>
                  </div>
                  <p class="capability-description">{language.t(integration.description, { name: integration.label })}</p>
                  <Show when={integration.managed && alphaProjects.latest?.length}><p class="capability-description">{language.t("datox.integrations.alphaDeskProjects", { projects: alphaProjects.latest!.join(", ") })}</p></Show>
                  <div class="capability-actions">
                  <Show when={integration.managed} fallback={
                  <button
                    type="button"
                    disabled={!integration.providerKey || !!store.busy || connections.loading || !!connections.error}
                    aria-busy={!!integration.providerKey && store.busy === integration.providerKey}
                    data-datox-action={integration.providerKey && !connectionForProvider(integration.providerKey) ? "primary" : "secondary"}
                    class="capability-button"
                    onClick={() => {
                      if (!integration.providerKey) return
                      const connection = connectionForProvider(integration.providerKey)
                      if (connection) return disconnect(integration.providerKey, connection.connection_id)
                      connect(integration.providerKey)
                    }}
                  >
                    {language.t(!integration.providerKey ? "datox.integrations.comingSoon" : store.busy === integration.providerKey ? "datox.integrations.working" : connectionForProvider(integration.providerKey) ? "datox.integrations.disconnect" : "datox.integrations.connect")}
                  </button>
                  }><span class="text-13-regular text-v2-text-text-muted">{language.t("datox.integrations.managedByAdmin")}</span></Show>
                  </div>
                </div>
              )}
            </For>
          </div>
        </div>
      </div>
    </div>
  )
}

export const datoxIntegrationCatalog: readonly DatoxIntegrationCatalogItem[] = [
  { label: "Alpha Desk", description: "datox.integrations.description.alphaDesk", canConnect: false, managed: true },
  { label: "Google Drive", logoPath: "/template-logos/google-drive.svg", description: "datox.integrations.description.google-drive", canConnect: true, providerKey: "google-drive" },
  { label: "OneDrive", logoPath: "/template-logos/one-drive.svg", description: "datox.integrations.description.one-drive", canConnect: true, providerKey: "one-drive" },
  { label: "SharePoint", logoPath: "/template-logos/sharepoint-online.svg", description: "datox.integrations.description.sharepoint-online", canConnect: true, providerKey: "sharepoint-online" },
  { label: "Dropbox", logoPath: "/template-logos/dropbox.svg", description: "datox.integrations.description.dropbox", canConnect: false },
  { label: "Box", logoPath: "/template-logos/box.svg", description: "datox.integrations.description.box", canConnect: false },
  { label: "Notion", logoPath: "/template-logos/notion.svg", description: "datox.integrations.description.notion", canConnect: false },
  { label: "Confluence", logoPath: "/template-logos/confluence.svg", description: "datox.integrations.description.confluence", canConnect: false },
  { label: "Jira", logoPath: "/template-logos/jira.svg", description: "datox.integrations.description.jira", canConnect: false },
  { label: "GitHub", logoPath: "/template-logos/github.svg", description: "datox.integrations.description.github", canConnect: false },
  { label: "GitLab", logoPath: "/template-logos/gitlab.svg", description: "datox.integrations.description.gitlab", canConnect: false },
  { label: "Slack", logoPath: "/template-logos/slack.svg", description: "datox.integrations.description.slack", canConnect: false },
  { label: "Microsoft Teams", logoPath: "/template-logos/microsoft-teams.svg", description: "datox.integrations.description.microsoft-teams", canConnect: false },
  { label: "Amazon S3", logoPath: "/template-logos/aws.svg", description: "datox.integrations.description.aws", canConnect: false },
  { label: "Google Cloud", logoPath: "/template-logos/google-cloud-storage.svg", description: "datox.integrations.description.google-cloud-storage", canConnect: false },
  { label: "Azure Blob Storage", logoPath: "/template-logos/azure-blob-storage.svg", description: "datox.integrations.description.azure-blob-storage", canConnect: false },
  { label: "Figma", logoPath: "/template-logos/figma.svg", description: "datox.integrations.description.figma", canConnect: false },
  { label: "Airtable", logoPath: "/template-logos/airtable.svg", description: "datox.integrations.description.airtable", canConnect: false },
  { label: "Salesforce", logoPath: "/template-logos/salesforce.svg", description: "datox.integrations.description.salesforce", canConnect: false },
  { label: "HubSpot", logoPath: "/template-logos/hubspot.svg", description: "datox.integrations.description.hubspot", canConnect: false },
  { label: "Asana", logoPath: "/template-logos/asana.svg", description: "datox.integrations.description.asana", canConnect: false },
  { label: "Linear", logoPath: "/template-logos/linear.svg", description: "datox.integrations.description.linear", canConnect: false },
  { label: "Trello", logoPath: "/template-logos/trello.svg", description: "datox.integrations.description.trello", canConnect: false },
  { label: "Zendesk", logoPath: "/template-logos/zendesk.svg", description: "datox.integrations.description.zendesk", canConnect: false },
  { label: "Intercom", logoPath: "/template-logos/intercom.svg", description: "datox.integrations.description.intercom", canConnect: false },
  { label: "Webflow", logoPath: "/template-logos/webflow.svg", description: "datox.integrations.description.webflow", canConnect: false },
  { label: "WordPress", logoPath: "/template-logos/wordpress.svg", description: "datox.integrations.description.wordpress", canConnect: false },
  { label: "1Password", logoPath: "/template-logos/1password.svg", description: "datox.integrations.description.iam", canConnect: false },
  { label: "ActiveCampaign", logoPath: "/template-logos/active-campaign.svg", description: "datox.integrations.description.marketing", canConnect: false },
  { label: "Adobe", logoPath: "/template-logos/adobe.svg", description: "datox.integrations.description.productivity", canConnect: false },
  { label: "ADP", logoPath: "/template-logos/adp.svg", description: "datox.integrations.description.hr", canConnect: false },
  { label: "Affinity", logoPath: "/template-logos/affinity.svg", description: "datox.integrations.description.crmGeneric", canConnect: false },
  { label: "Apollo", logoPath: "/template-logos/apollo.svg", description: "datox.integrations.description.marketing", canConnect: false },
  { label: "Attio", logoPath: "/template-logos/attio.svg", description: "datox.integrations.description.crmGeneric", canConnect: false },
  { label: "AutoTask", logoPath: "/template-logos/autotask.svg", description: "datox.integrations.description.ticketing", canConnect: false },
  { label: "Azure DevOps", logoPath: "/template-logos/azure-devops.svg", description: "datox.integrations.description.devtools", canConnect: false },
  { label: "BambooHR", logoPath: "/template-logos/bamboohr.svg", description: "datox.integrations.description.hr", canConnect: false },
  { label: "Basecamp", logoPath: "/template-logos/basecamp.svg", description: "datox.integrations.description.productivity", canConnect: false },
  { label: "Bitbucket", logoPath: "/template-logos/bitbucket.svg", description: "datox.integrations.description.devtools", canConnect: false },
  { label: "Braze", logoPath: "/template-logos/braze.svg", description: "datox.integrations.description.marketing", canConnect: false },
  { label: "Calendly", logoPath: "/template-logos/calendly.svg", description: "datox.integrations.description.productivity", canConnect: false },
  { label: "Canvas LMS", logoPath: "/template-logos/canvas-lms.svg", description: "datox.integrations.description.knowledge", canConnect: false },
  { label: "ClickUp", logoPath: "/template-logos/clickup.svg", description: "datox.integrations.description.productivity", canConnect: false },
  { label: "Contentful", logoPath: "/template-logos/contentful.svg", description: "datox.integrations.description.content", canConnect: false },
  { label: "Copper", logoPath: "/template-logos/copper.svg", description: "datox.integrations.description.crmGeneric", canConnect: false },
  { label: "DocuSign", logoPath: "/template-logos/docusign.svg", description: "datox.integrations.description.documents", canConnect: false },
  { label: "Drata", logoPath: "/template-logos/drata.svg", description: "datox.integrations.description.compliance", canConnect: false },
  { label: "Freshdesk", logoPath: "/template-logos/freshdesk.svg", description: "datox.integrations.description.ticketing", canConnect: false },
  { label: "Gong", logoPath: "/template-logos/gong.svg", description: "datox.integrations.description.sales", canConnect: false },
  { label: "Google Ads", logoPath: "/template-logos/google-ads.svg", description: "datox.integrations.description.marketing", canConnect: false },
  { label: "Greenhouse", logoPath: "/template-logos/greenhouse.svg", description: "datox.integrations.description.hr", canConnect: false },
  { label: "Gusto", logoPath: "/template-logos/gusto.svg", description: "datox.integrations.description.hr", canConnect: false },
  { label: "Ironclad", logoPath: "/template-logos/ironclad.svg", description: "datox.integrations.description.documents", canConnect: false },
  { label: "Klaviyo", logoPath: "/template-logos/klaviyo.svg", description: "datox.integrations.description.marketing", canConnect: false },
  { label: "Lever", logoPath: "/template-logos/lever.svg", description: "datox.integrations.description.hr", canConnect: false },
  { label: "Looker", logoPath: "/template-logos/looker.svg", description: "datox.integrations.description.analytics", canConnect: false },
  { label: "Mailchimp", logoPath: "/template-logos/mailchimp.svg", description: "datox.integrations.description.marketing", canConnect: false },
  { label: "Mailgun", logoPath: "/template-logos/mailgun.svg", description: "datox.integrations.description.communication", canConnect: false },
  { label: "Marketo", logoPath: "/template-logos/marketo.svg", description: "datox.integrations.description.marketing", canConnect: false },
  { label: "Miro", logoPath: "/template-logos/miro.svg", description: "datox.integrations.description.productivity", canConnect: false },
  { label: "Mixpanel", logoPath: "/template-logos/mixpanel.svg", description: "datox.integrations.description.analytics", canConnect: false },
  { label: "Monday.com", logoPath: "/template-logos/monday.svg", description: "datox.integrations.description.productivity", canConnect: false },
  { label: "OpenAI", logoPath: "/template-logos/openai.svg", description: "datox.integrations.description.ai", canConnect: false },
  { label: "Paychex", logoPath: "/template-logos/paychex.svg", description: "datox.integrations.description.hr", canConnect: false },
  { label: "Personio", logoPath: "/template-logos/personio.svg", description: "datox.integrations.description.hr", canConnect: false },
  { label: "QuickBooks", logoPath: "/template-logos/quickbooks.svg", description: "datox.integrations.description.accounting", canConnect: false },
  { label: "Sage Intacct", logoPath: "/template-logos/sage-intacct.svg", description: "datox.integrations.description.accounting", canConnect: false },
  { label: "ServiceNow", logoPath: "/template-logos/servicenow.svg", description: "datox.integrations.description.ticketing", canConnect: false },
  { label: "Shopify", logoPath: "/template-logos/shopify.svg", description: "datox.integrations.description.ecommerce", canConnect: false },
  { label: "Stripe", logoPath: "/template-logos/stripe.svg", description: "datox.integrations.description.payments", canConnect: false },
  { label: "Workday", logoPath: "/template-logos/workday.svg", description: "datox.integrations.description.hr", canConnect: false },
  { label: "Workable", logoPath: "/template-logos/workable.svg", description: "datox.integrations.description.hr", canConnect: false },
  { label: "Xero", logoPath: "/template-logos/xero.svg", description: "datox.integrations.description.accounting", canConnect: false },
  { label: "Zoho CRM", logoPath: "/template-logos/zoho-crm.svg", description: "datox.integrations.description.crmGeneric", canConnect: false },
  { label: "Zoom", logoPath: "/template-logos/zoom.svg", description: "datox.integrations.description.communication", canConnect: false },
] as const

const libraryFileTypes = ["all", "images", "documents", "spreadsheets"] as const

function libraryFileTypeLabel(type: LibraryFileType) {
  return `datox.files.library.filter.${type}`
}

function fileTypeMatches(file: LibraryFile, type: LibraryFileType) {
  if (type === "all") return true
  if (type === "images") return file.content_type.startsWith("image/")
  if (type === "spreadsheets") return /spreadsheet|csv|excel/i.test(file.content_type) || /\.(xlsx|xls|csv)$/i.test(file.filename)
  return /pdf|wordprocessing|text|json|xml/i.test(file.content_type) || /\.(pdf|doc|docx|txt|json|xml)$/i.test(file.filename)
}

function formatLibraryDate(value: string) {
  return new Date(value).toLocaleDateString(undefined, { month: "short", day: "numeric" })
}

function formatLibrarySize(bytes: number) {
  if (bytes < 1024) return new Intl.NumberFormat(undefined, { style: "unit", unit: "byte", unitDisplay: "short" }).format(bytes)
  if (bytes < 1024 * 1024) return new Intl.NumberFormat(undefined, { style: "unit", unit: "kilobyte", unitDisplay: "short", maximumFractionDigits: 1 }).format(bytes / 1024)
  return new Intl.NumberFormat(undefined, { style: "unit", unit: "megabyte", unitDisplay: "short", maximumFractionDigits: 1 }).format(bytes / 1024 / 1024)
}

function toggleNavigation() {
  const key = "datox.agent.navigationCollapsed"
  const next = localStorage.getItem(key) !== "true"
  localStorage.setItem(key, String(next))
  window.dispatchEvent(new CustomEvent("datox:navigation-toggle", { detail: { collapsed: next } }))
}

function storedNavigationCollapsed() {
  try {
    return localStorage.getItem("datox.agent.navigationCollapsed") === "true"
  } catch {
    return false
  }
}

function storedNavigationWidth() {
  try {
    const value = Number(localStorage.getItem("datox.agent.navigationWidth"))
    return Number.isFinite(value) ? Math.min(420, Math.max(240, value)) : 288
  } catch {
    return 288
  }
}
